/**
 * The pivot model (SDD-024 P-3, P-4): the flat result of `GROUP BY ROLLUP (<rows…>, <columns…>)`
 * with `grouping(<rows…>, <columns…>) AS "__grouping"` turned into the grid the viewer draws.
 *
 * The placement of a subtotal comes from `__grouping`, never from the sort order (prompt 60's
 * blocker table: Postgres orders NULL subtotal rows differently from Athena). `grouping(a, b, c)`
 * is a bitmask whose MOST significant bit is the FIRST argument; a set bit means "aggregated over
 * this field" — so a row's depth is the number of leading row fields whose bit is clear.
 *
 * Shape of the grid: one row per row-field combination, parents (with their subtotals) above their
 * children, the grand total last; one column group per combination of the column fields as the
 * server ordered them, plus "Gesamt" when there are column fields; inside every group one cell per
 * value. Partial column subtotals (the first of two column levels) are not drawn (§14.6). At most
 * {@link MAX_COLUMN_GROUPS} column groups are drawn; the rest is reported as cut (SDD-024 Q-4).
 */

export type ResultRole = 'ROW' | 'COLUMN' | 'VALUE' | 'FIELD' | 'GROUPING' | 'CURRENCY';

/** One result column as the service names it (`dataservice.v1.QueryColumn`). */
export interface ResultColumn {
  readonly key: string;
  readonly label: string;
  readonly type: string;
  readonly role: ResultRole;
  readonly index: number;
}

/** A result row: the provider's text or null per column. */
export type ResultRow = readonly (string | null)[];

/** SDD-024 Q-4: the renderer draws at most this many column groups (decided in §14.6). */
export const MAX_COLUMN_GROUPS = 60;

export const TOTAL_KEY = '__total';

export interface PivotColumnGroup {
  /** Stable key: the JSON of the column values, or {@link TOTAL_KEY}. */
  readonly key: string;
  /** The raw values of the column fields (empty for the total). */
  readonly values: readonly (string | null)[];
  readonly total: boolean;
}

export interface PivotRow {
  readonly key: string;
  /** 0 = the grand total; 1 … rowFields = the level of the deepest field it names. */
  readonly depth: number;
  /** The raw values of the row fields down to `depth`. */
  readonly path: readonly (string | null)[];
  /** A parent row (depth < row fields) or the grand total. */
  readonly subtotal: boolean;
  readonly total: boolean;
  /** Cells per column group key: one raw value per pivot value (null = no row for it). */
  readonly cells: Readonly<Record<string, readonly (string | null)[]>>;
}

export interface PivotModel {
  readonly rowFields: readonly ResultColumn[];
  readonly columnFields: readonly ResultColumn[];
  readonly values: readonly ResultColumn[];
  readonly columnGroups: readonly PivotColumnGroup[];
  readonly rows: readonly PivotRow[];
  /** More column groups than {@link MAX_COLUMN_GROUPS}: the rest are not drawn. */
  readonly columnsCut: boolean;
}

const byIndex = (a: ResultColumn, b: ResultColumn): number => a.index - b.index;

interface Node {
  readonly key: string;
  readonly depth: number;
  readonly path: (string | null)[];
  cells: Record<string, (string | null)[]>;
  hasOwnRow: boolean;
  readonly children: Node[];
}

export function buildPivot(
  columns: readonly ResultColumn[],
  rows: readonly ResultRow[],
): PivotModel {
  const positions = new Map(columns.map((c, i) => [c, i] as const));
  const at = (row: ResultRow, column: ResultColumn): string | null =>
    row[positions.get(column) ?? -1] ?? null;
  const rowFields = columns.filter((c) => c.role === 'ROW').sort(byIndex);
  const columnFields = columns.filter((c) => c.role === 'COLUMN').sort(byIndex);
  const values = columns.filter((c) => c.role === 'VALUE').sort(byIndex);
  const grouping = columns.find((c) => c.role === 'GROUPING');
  const arity = rowFields.length + columnFields.length;

  const root: Node = { key: '[]', depth: 0, path: [], cells: {}, hasOwnRow: false, children: [] };
  const nodes = new Map<string, Node>([[root.key, root]]);
  const groups = new Map<string, PivotColumnGroup>();
  let hasTotalColumn = false;

  const nodeFor = (path: (string | null)[]): Node => {
    let parent = root;
    for (let depth = 1; depth <= path.length; depth++) {
      const prefix = path.slice(0, depth);
      const key = JSON.stringify(prefix);
      let node = nodes.get(key);
      if (node === undefined) {
        node = { key, depth, path: prefix, cells: {}, hasOwnRow: false, children: [] };
        nodes.set(key, node);
        parent.children.push(node);
      }
      parent = node;
    }
    return parent;
  };

  for (const row of rows) {
    const mask = grouping === undefined ? 0 : Number(at(row, grouping) ?? '0');
    const bit = (i: number): boolean => ((mask >> (arity - 1 - i)) & 1) === 1;
    let rowDepth = 0;
    while (rowDepth < rowFields.length && !bit(rowDepth)) rowDepth++;
    let colDepth = 0;
    while (colDepth < columnFields.length && !bit(rowFields.length + colDepth)) colDepth++;

    let groupKey: string;
    if (columnFields.length === 0) {
      groupKey = TOTAL_KEY;
    } else if (colDepth === columnFields.length) {
      const colValues = columnFields.map((c) => at(row, c));
      groupKey = JSON.stringify(colValues);
      if (!groups.has(groupKey))
        groups.set(groupKey, { key: groupKey, values: colValues, total: false });
    } else if (colDepth === 0) {
      groupKey = TOTAL_KEY;
      hasTotalColumn = true;
    } else {
      continue; // a partial column subtotal: not drawn
    }

    const node = nodeFor(rowFields.slice(0, rowDepth).map((c) => at(row, c)));
    node.cells[groupKey] = values.map((v) => at(row, v));
    node.hasOwnRow = true;
  }

  const allGroups = [...groups.values()];
  const columnsCut = allGroups.length > MAX_COLUMN_GROUPS;
  const columnGroups: PivotColumnGroup[] = allGroups.slice(0, MAX_COLUMN_GROUPS);
  if (columnFields.length === 0 || hasTotalColumn) {
    columnGroups.push({ key: TOTAL_KEY, values: [], total: true });
  }

  const out: PivotRow[] = [];
  const visit = (node: Node): void => {
    out.push({
      key: node.key,
      depth: node.depth,
      path: node.path,
      subtotal: node.depth < rowFields.length,
      total: false,
      cells: node.cells,
    });
    for (const child of node.children) visit(child);
  };
  for (const child of root.children) visit(child);
  if (root.hasOwnRow || rowFields.length === 0) {
    out.push({
      key: root.key,
      depth: 0,
      path: [],
      subtotal: rowFields.length > 0,
      total: true,
      cells: root.cells,
    });
  }

  return { rowFields, columnFields, values, columnGroups, rows: out, columnsCut };
}
